import EventEmitter from 'events';
import ZoneSelectList from './controls/ZoneSelectList';
import ZoneRadioButtonList from './controls/ZoneRadioButtonList';
import dom from '../../../utils/dom';
import ZoneGroupSelector from '../ZoneGroupSelector';

class ZoneSelector extends EventEmitter {
  /** @type {string} */
  zoneId;

  /** @type {string} */
  zoneGroupId;

  /**
   * @private
   * @type {HTMLElement|null} */
  soldOutContainer;

  /**
   * @private
   * @type {HTMLLIElement[]} */
  soldOutItems;

  /**
   * @private
   * @type {ZoneSelectList|ZoneRadioButtonList}
   * */
  control;

  /**
   * @param {HTMLElement} el
   * @param {HTMLElement} groupSelectorContainer
   */
  constructor(el, groupSelectorContainer) {
    super();

    this.el = el;

    const inputs = dom.findAllFrom(this.el, '.tn-ticket-selector__input-zone');

    if (inputs[0].tagName === 'SELECT') {
      this.control = new ZoneSelectList(inputs[0]);
    } else {
      const listItems = dom.findAllFrom(this.el, '.tn-zone-selector__option');
      this.control = new ZoneRadioButtonList(listItems);
    }

    this.zoneGroupSelector = new ZoneGroupSelector(groupSelectorContainer, (zoneGroupId) => {
      this.selectZoneGroup(zoneGroupId);
    });

    this.control.on('change', (zoneId) => {
      this.selectZone(zoneId);
    });

    /** @type {HTMLElement|null} */
    const containerRoot = this.el.closest
      ? (this.el.closest('.tn-ticket-selector__zone-selector-container') || this.el)
      : this.el;

    this.soldOutContainer = dom.findFrom(containerRoot, 'section.tn-ticket-selector__sold-out-section');
    this.soldOutItems = this.soldOutContainer
      ? dom.findAllFrom(this.soldOutContainer, 'li.tn-ticket-selector__sold-out-zone')
      : [];
  }

  getSelectedZoneId() {
    return this.zoneId;
  }

  getSelectedZoneGroupId() {
    return this.zoneGroupId;
  }

  selectZone = (zoneId) => {
    if (zoneId === this.zoneId) {
      return;
    }

    if (zoneId && !this.control.getIsZoneAvailable(zoneId)) {
      return;
    }

    let { zoneGroupId } = this;

    if (this.zoneGroupSelector.isSingleZoneGroup) {
      zoneGroupId = this.zoneGroupSelector.getZoneGroupIds()[0];
    } else if (zoneId) {
      zoneGroupId = this.control.getZoneGroupIdForZone(zoneId);
    }

    this.update(zoneId, zoneGroupId);
  }

  /**
   * @private
   * @param {string} zoneGroupId
   */
  selectZoneGroup(zoneGroupId = '') {
    if (zoneGroupId === this.zoneGroupId) {
      return;
    }

    if (zoneGroupId && !this.control.getIsZoneGroupAvailable(zoneGroupId)) {
      return;
    }

    let zoneId = '';

    if (zoneGroupId) {
      zoneId = this.control.getSelectedZoneIdForZoneGroup(zoneGroupId);
    }

    this.update(zoneId, zoneGroupId);
  }

  autoSelectSingleZone() {
    const zoneIds = this.control.getZoneIds();

    if (zoneIds.length === 1) {
      this.selectZone(zoneIds[0]);
    }
  }

  /**
   * @private
   * @param {string} zoneId
   * @param {string} zoneGroupId
   */
  update(zoneId, zoneGroupId) {
    const isZoneChange = zoneId !== this.zoneId;
    const isZoneGroupChange = zoneGroupId !== this.zoneGroupId;

    if (!isZoneChange && !isZoneGroupChange) {
      return;
    }

    this.zoneId = zoneId;
    this.zoneGroupId = zoneGroupId;

    if (zoneGroupId) {
      dom.unhide(this.el);
    } else {
      dom.hide(this.el);
    }

    this.updateSoldOutView(zoneGroupId);

    if (isZoneGroupChange) {
      this.zoneGroupSelector.selectZoneGroup(zoneGroupId);
      this.control.selectZoneGroup(zoneGroupId);
    }

    if (isZoneChange) {
      this.control.selectZone(zoneId);
    }

    this.emit('update');
  }

  /**
   * @private
   * @param {string} zoneGroupId
   */
  updateSoldOutView(zoneGroupId) {
    if (!this.soldOutContainer) {
      return;
    }

    if (!zoneGroupId) {
      dom.hide(this.soldOutContainer);
      return;
    }

    let anyVisible = false;
    this.soldOutItems.forEach((item) => {
      const itemGroupId = item.getAttribute('data-tn-zone-group-id') || '';
      const shouldShow = itemGroupId === zoneGroupId;
      if (shouldShow) {
        dom.unhide(item);
        anyVisible = true;
      } else {
        dom.hide(item);
      }
    });

    this.soldOutContainer.style.display = anyVisible ? 'block' : 'none';
  }

  destroy() {
    this.removeAllListeners();
    this.control.destroy();
    this.zoneGroupSelector.destroy();
  }
}

export default ZoneSelector;
